import { CSSProperties } from 'react';
import { useTableSelector } from 'hooks/useTableSelector';

import { useMaxRunCount } from 'components/UserActions/MultiRunActions/maxRunCount';
import { initiatorRunCounts } from 'components/UserActions/MultiRunActions/multiRunPhase';
import { RUN_VARIANT } from 'components/UserActions/MultiRunActions/runVariants';
import { runLabel } from 'components/UserActions/MultiRunActions/voteFromAction';
import {
  parseVotePhaseKey,
  votePhaseKey
} from 'components/UserActions/MultiRunActions/votePresentation';
import { actionVariants } from 'components/UserActions/PokerActions/actionVariants';

import styles from './MultiRunVoteText.module.scss';

/**
 * The felt-level line for the multi-run vote (EN-10856 AC-2 / AC-5). Every seat
 * reads it, folded players included, and it is the vote's call to action — the
 * 9/17 demo's "something needs to draw their eyes" — so the tray needs no
 * cue of its own:
 *   - choosing → RUN IT [1X] [2X] [3X] ? — one sentence, the question the
 *                initiator is answering (Jason, 2026-09-24; EN-663 N-9 bars
 *                question copy, so the contract needs Ryan's amendment)
 *   - offered  → RUN IT 2X / RUN IT 3X, in that count's colour
 *   - once     → RUN IT 1X in blue, for every run-once outcome (MR-22)
 * Nothing while the boards run out: the fork and the run captions take over.
 * No names, no seconds (the window is configurable per table template).
 */
const chipVars = (runs: number): CSSProperties => {
  const variant = actionVariants[RUN_VARIANT[runs] ?? 'check'];
  return {
    ['--run-bg' as string]: variant.bg,
    ['--run-border' as string]: variant.border,
    ['--run-dash' as string]: variant.dash,
    ['--run-text' as string]: variant.text,
    ['--run-shadow' as string]: variant.shadow
  } as CSSProperties;
};

const MultiRunVoteText = () => {
  const key = useTableSelector((table) => votePhaseKey(table.game));
  // EN-10921: the question on the felt lists exactly the initiator's buttons,
  // so it drops 3X while the ops switch withholds it.
  const maxRunCount = useMaxRunCount();
  const { phase, offer } = parseVotePhaseKey(key);

  if (phase === 'choosing') {
    return (
      <div className={styles.anchor}>
        <span className={styles.line} data-multi-run-text='choosing'>
          <span className={styles.lead}>RUN IT</span>
          {initiatorRunCounts(maxRunCount).map((runs) => (
            <span key={runs} className={styles.count} style={chipVars(runs)}>
              {runs}X
            </span>
          ))}
          <span>?</span>
        </span>
      </div>
    );
  }

  const runs = phase === 'offered' ? offer : phase === 'once' ? 1 : 0;
  if (runs === 0) return null;

  return (
    <div className={styles.anchor}>
      <span
        className={`${styles.line} ${styles.decided}`}
        style={chipVars(runs)}
        data-multi-run-text={phase}>
        {runLabel(runs)}
      </span>
    </div>
  );
};

export default MultiRunVoteText;
